import type { ClientFlowAction } from '~/graphql/types.client';

/**
 * For existing (non-cloned) actions that had a secondary-parent relationship
 * with any original copied action, adds the cloned counterpart as an additional
 * secondary parent so portal relationships are preserved after paste.
 *
 * Primary parents (index 0) are intentionally skipped — the standard paste
 * wiring logic handles them separately.
 */
const addClonedSecondaryParents = (
  action: ClientFlowAction,
  copiedOriginalIds: Set<string>,
  clonedIdByOriginalId: Map<string, string>,
): ClientFlowAction => {
  if (!('parentIds' in action)) return action;
  if (copiedOriginalIds.has(action.id)) return action;

  const prevParents = [...action.parentIds];
  let changed = false;

  for (const [originalParentId, clonedParentId] of clonedIdByOriginalId) {
    const idx = prevParents.indexOf(originalParentId);

    // Only duplicate secondary-parent relations (portals). If the original
    // action is the primary parent, the standard paste wiring logic handles it.
    if (idx <= 0) continue;
    if (prevParents.includes(clonedParentId)) continue;

    prevParents.push(clonedParentId);
    changed = true;
  }

  return changed
    ? ({ ...action, parentIds: prevParents } as ClientFlowAction)
    : action;
};

export default addClonedSecondaryParents;
